import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Grid, Row } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import overlayFactory from 'react-bootstrap-table2-overlay';
import { useTranslation } from 'react-i18next';
import { Warning } from '../../../components/Notifications/Notifications';
import { Remove } from '../../../components/Icons/Icons';
import { Spinner } from '../../../components/Utils/Loaders';
import { VisitStages } from '../../../components/Protocols/Visit/Enums';
import { useDeleteFormFromVisits } from './useForms';

export const DeleteFormFromVisitsContainer = ({ protocolId, visitId, formTitle, onSubmitted, onCancel }) => {
    const { t } = useTranslation();
    const [fetchVisitsWithSameForm, visits, isLoadingVisits, deleteFromVisits, isDeleting] = useDeleteFormFromVisits();
    const [selectedVisitIds, setSelectedVisitIds] = useState([]);
    const fetchStartedRef = useRef(false);

    useEffect(() => {
        if (!fetchStartedRef.current || isLoadingVisits) return;
        if (visits.length === 0) {
            onSubmitted();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingVisits]);

    useEffect(() => {
        fetchStartedRef.current = true;
        fetchVisitsWithSameForm(protocolId, visitId, formTitle);
    }, [protocolId, visitId, formTitle]);

    const handleDelete = async () => {
        if (!selectedVisitIds || selectedVisitIds.length === 0) return;

        const response = await deleteFromVisits(protocolId, visitId, formTitle, selectedVisitIds);
        if (!response) return;

        const { failures } = response.data;
        if (failures && failures.length > 0) {
            Warning("protocolNotifications.form_DeletePartialError");
        }

        onSubmitted();
    };

    const columns = [
        {
            dataField: 'title',
            text: t("protocols.visit.visitName")
        },
        {
            dataField: 'stage',
            text: t("protocols.visit.visitStage"),
            formatter: (cell) => VisitStages().find(x => x.id === cell)?.title ?? cell
        },
    ];

    const selectRow = {
        mode: 'checkbox',
        onSelect: (row, isSelect) => {
            if (isSelect)
                setSelectedVisitIds(prev => prev.concat(row.crossVersionId));
            else
                setSelectedVisitIds(prev => prev.filter(id => id !== row.crossVersionId));
        },
        onSelectAll: (isSelect, rows) => {
            setSelectedVisitIds(isSelect ? rows.map(r => r.crossVersionId) : []);
        },
    };

    if (isDeleting)
        return <Spinner />;

    return (
        <>
            <Grid fluid>
                <Row>
                    <Col sm={12}>
                        <BootstrapTable
                            classes="table-spaced table-spaced-smallest table-spaced-small"
                            wrapperClasses="table-responsive"
                            bordered={false}
                            hover
                            remote
                            keyField='id'
                            data={visits}
                            columns={columns}
                            noDataIndication={() => <>{t("commons.noData")}</>}
                            loading={isLoadingVisits}
                            overlay={overlayFactory({ spinner: true })}
                            selectRow={selectRow}
                        />
                    </Col>
                </Row>
            </Grid>
            <Grid fluid style={{ marginTop: '10px' }}>
                <Row>
                    <Col sm={12}>
                        <button
                            onClick={handleDelete}
                            disabled={selectedVisitIds.length === 0}
                            className="btn btn-danger btn-right">
                            <Remove /> {t('protocols.visit.deleteFormFromVisits_confirm')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-default btn-right"
                            onClick={onCancel}
                            style={{ marginRight: '10px' }}>
                            {t("buttons.cancel")}
                        </button>
                    </Col>
                </Row>
            </Grid>
        </>
    );
};

DeleteFormFromVisitsContainer.propTypes = {
    protocolId: PropTypes.number.isRequired,
    visitId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    formTitle: PropTypes.string.isRequired,
    onSubmitted: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};
